import {
	DATE_FORMATS,
	DonationStatus,
	DonationTabs,
	DonationType,
	TableTitles,
} from "@/constants";
import { useWindowDimensions } from "@/hooks";
import {
	useDeleteDonationRequest,
	useGetDonationsByMeditationRetreat,
} from "@/hooks/query-hooks/useDonors";
import { useGetMeditationRetreats } from "@/hooks/query-hooks/useMeditationRetreats";
import { cn } from "@/libs/cn";
import { CLIENT_API } from "@/services/axios-client";
import { Button, DatePicker, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import download from "downloadjs";
import { useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { AiOutlineExport } from "react-icons/ai";
import { useLocation } from "react-router";
import { AnimatedComponent, DeleteConfirmationModal } from "../common";
import { AcceptDonationDrawer } from "./AcceptDonationDrawer";
import { DonationFilter } from "./DonationFilter";
import { DonationTable } from "./DonationTable";

export function Donations() {
	const searchParams = useLocation().search;
	const activeTab = new URLSearchParams(searchParams).get("tab");

	const [year, setYear] = useState(dayjs());
	const [retreatList, setRetreatList] = useState<
		{ label: string; value: number }[]
	>([]);
	const { data: retreatsData, isLoading: isRetreatsLoading } =
		useGetMeditationRetreats(1, year.year(), 100);

	const [page, setPage] = useState(1);
	const [retreatId, setRetreatId] = useState<number | undefined>(undefined);
	const [eventType, setEventType] = useState<DonationType>(
		DonationType.EVENT_ALM,
	);

	const { height } = useWindowDimensions();

	const { data: donationsData, isLoading: isDonationsLoading } =
		useGetDonationsByMeditationRetreat({
			retreatId,
			page,
			type: eventType,
			status: DonationStatus.ACCEPTED,
			queryProps: {
				enabled:
					(eventType !== DonationType.BOHI_ETERNAL_TREE
						? !!retreatId
						: true) &&
					!!eventType &&
					page > 0,
				refetchOnReconnect: true,
			},
		});

	useEffect(() => {
		if (eventType === DonationType.BOHI_ETERNAL_TREE) return;
		const retreats = retreatsData?.content || [];
		if (retreats && retreats.length > 0) {
			const retreatList = retreats?.map((retreat) => ({
				label:
					retreat.title +
					` (${retreat.level.name} - ${dayjs(retreat.fromDate).format(
						DATE_FORMATS.MONTH_FORMAT,
					)})`,
				value: retreat.id,
			}));
			setRetreatList(retreatList || []);
			setRetreatId(retreatList?.[0]?.value);
		} else {
			setRetreatList([]);
			setRetreatId(undefined);
		}
	}, [retreatsData, year, eventType]);

	useEffect(() => {
		if (activeTab) {
			switch (activeTab) {
				case DonationTabs.alms_donors:
					setEventType(DonationType.EVENT_ALM);
					setRetreatId(retreatList?.[0]?.value); // Set retreatId to the first retreat by default
					break;
				case DonationTabs.founding_donors:
					setEventType(DonationType.DISCRETIONARY_MONEY);
					setRetreatId(retreatList?.[0]?.value); // Set retreatId to the first retreat by default
					break;
				case DonationTabs.padetha_donors:
					setEventType(DonationType.BOHI_ETERNAL_TREE);
					setRetreatId(undefined); // Reset retreatId for padetha donors
					break;
				default:
					setEventType(DonationType.EVENT_ALM);
					setRetreatId(retreatList?.[0]?.value);
					break;
			}
		}
	}, [activeTab, retreatList]);

	const onYearChange = (value: Dayjs) => {
		setYear(value);
		setRetreatId(undefined);
	};

	const renderColumnKeys = [
		TableTitles.donation_date,
		TableTitles.donor_name,
		TableTitles.donor_phone,
		TableTitles.donor_address,
		TableTitles.donation_type,
		TableTitles.donation_amount,
		TableTitles.action,
	];

	const handleExportExcel = async () => {
		try {
			await CLIENT_API.exportDonorsByMeditationRetreat(
				Number(retreatId!),
				eventType,
			).then((res) => {
				const fileName = `Donors_In_${eventType}.xlsx`;

				download(
					res,
					fileName,
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				);
			});
		} catch (error) {
			console.error("Error exporting donations:", error);
		}
	};

	return (
		<AnimatedComponent className="space-y-6">
			<Helmet>
				<title>အလှူရှင်များစာရင်း</title>
			</Helmet>
			<DonationFilter />
			<div className="flex items-center justify-between gap-4">
				{activeTab !== DonationTabs.padetha_donors && (
					<>
						<Select
							options={retreatList}
							value={retreatId}
							showSearch
							filterOption={(input, option) =>
								!!option &&
								option.label
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							onChange={(value) => setRetreatId(value as number)}
							placeholder="တရားစခန်းရွေးချယ်ပါ"
							size="large"
							className="w-[300px]"
							loading={isRetreatsLoading}
						/>
						<DatePicker.YearPicker
							value={year}
							onChange={onYearChange}
							placeholder="နှစ်ရွေးချယ်ပါ"
							size="large"
							className="w-[200px]"
							inputReadOnly
							allowClear={false}
						/>
					</>
				)}

				{eventType !== DonationType.BOHI_ETERNAL_TREE && (
					<Button
						size="large"
						className={cn("!font-poppins !ml-auto", {
							"bg-primary-gradient !text-white":
								donationsData?.content.length,
						})}
						icon={<AiOutlineExport />}
						onClick={handleExportExcel}
						disabled={!retreatId || !donationsData?.content.length}
					>
						Export
					</Button>
				)}
			</div>
			<DonationTable
				renderColumnKeys={renderColumnKeys}
				dataSource={donationsData?.content || []}
				loading={isRetreatsLoading || isDonationsLoading}
				pagination={{
					current: page,
					total: donationsData?.totalElements || 0,
					pageSize: donationsData?.size || 10,
					onChange: (page) => setPage(page),
				}}
				scroll={{
					y: height - 340,
				}}
				TableActionComponent={TableActionsComponent}
			/>
		</AnimatedComponent>
	);
}

const TableActionsComponent = ({ record }: { record: Donation }) => {
	const { mutate, isMutating } = useDeleteDonationRequest();

	return (
		<>
			<AcceptDonationDrawer donation={record} />
			<DeleteConfirmationModal
				onDelete={() => mutate(record.id)}
				isDeleting={isMutating}
				description="လှူဒါန်းမှုကို ဖျက်သိမ်းလိုပါသလား?"
				buttonProps={{
					danger: true,
					type: "text",
					size: "small",
				}}
			/>
		</>
	);
};
